import { useCallback } from 'react';

import { checkoutStateModel, type TGlobalDataModel, type TGlobalDataModelAuditLog } from '@/_models';
import { dateTime } from '@/_shared';

import { useLog } from './use-log';

export function useSynchingCache(
  data: TGlobalDataModel,
  updateGlobalData: (globalData: TGlobalDataModel, isOverride?: boolean, overrideLock?: boolean) => void,
  overrideGlobalData: (globalData: TGlobalDataModel, isOverride?: boolean) => void
) {
  const { auditLog, checkout } = data;
  const { getCustomerData } = useLog({ auditLog });

  const setCustomerTermsReviewed = useCallback(() => {
    const nAuditLog = getCustomerData();
    updateGlobalData({
      ...data,
      auditLog: nAuditLog,
      checkout: {
        ...checkout,
        termsReviewedByCustomer: true,
        syncDateStamp: dateTime.now().toISOString(),
      },
    });
  }, [checkout, data, getCustomerData, updateGlobalData]);

  const setPaymentProcessed = useCallback(() => {
    const nAuditLog = getCustomerData();
    updateGlobalData(
      {
        ...data,
        auditLog: nAuditLog,
        checkout: { ...checkout, paymentProcessed: true, paymentProcessingTimeStamp: dateTime.now().toISOString() },
      },
      true
    );
  }, [checkout, data, getCustomerData, updateGlobalData]);

  const setAcceptTerms = useCallback(
    (termsAccepted: 'YES' | 'NO') => {
      const nAuditLog = getCustomerData();
      const dto = {
        ...data,
        auditLog: nAuditLog,
        isLocked: termsAccepted === 'YES',
        checkout: {
          ...checkout,
          termsAccepted,
          termsAcceptanceTimeStamp: dateTime.now().toISOString(),
        },
      };

      overrideGlobalData(dto, true);
      return dto;
    },
    [checkout, data, getCustomerData, overrideGlobalData]
  );

  const resetCustomerConsent = useCallback(() => {
    const dto = {
      ...data,
      isLocked: false,
      checkout: {
        ...checkout,
        card_1: { ...checkoutStateModel.card_1 },
        card_2: { ...checkoutStateModel.card_2 },
        termsAccepted: '' as 'YES' | 'NO' | '',
        paymentProcessed: false,
        termsReviewedByCustomer: false,
        hasReviewedTerms: false,
        password: checkoutStateModel.password,
      },
      auditLog: {} as TGlobalDataModelAuditLog,
    };

    updateGlobalData(dto, true, true);
    return dto;
  }, [checkout, data, updateGlobalData]);

  return {
    setCustomerTermsReviewed,
    setPaymentProcessed,
    setAcceptTerms,
    resetCustomerConsent,
  };
}
